import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import logoUrl from '../assets/img/amart-logo.png';
import heroUrl from '../assets/img/amart-hero.png';

interface LocationState {
  from?: string;
}

type Mode = 'password' | 'otp';

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12.5l4.5 4.5L19 6.5" />
  </svg>
);

export function Login() {
  const { user, signIn, requestOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [keep, setKeep] = useState(true);
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (user) {
    const from = (location.state as LocationState | null)?.from ?? '/dashboard';
    return <Navigate to={from} replace />;
  }

  const linkError = new URLSearchParams(location.search).get('error');

  function goHome() {
    const from = (location.state as LocationState | null)?.from ?? '/dashboard';
    navigate(from, { replace: true });
  }

  function switchMode(next: Mode) {
    setMode(next);
    setError(null);
    setInfo(null);
    setCode('');
    setCodeSent(false);
  }

  async function onPasswordSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email.trim(), password);
      goHome();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function onRequestCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      await requestOtp(email.trim());
      setCodeSent(true);
      setInfo(`If an account exists for ${email.trim()}, a 6-digit code is on its way.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send a code');
    } finally {
      setSubmitting(false);
    }
  }

  async function onVerifyCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await verifyOtp(email.trim(), code.trim());
      goHome();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not verify the code');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="signin af-light">
      {/* LEFT — cream sign-in panel */}
      <div className="signin-left">
        <div className="signin-topbar">
          <img className="signin-logo" src={logoUrl} alt="Amart" />
          <a className="signin-toplink" href="https://www.amart.com.au" target="_blank" rel="noreferrer">
            amart.com.au&nbsp;→
          </a>
        </div>

        <div className="signin-body">
          <h1 className="signin-title">
            Reporting<br />
            <span>portal.</span>
          </h1>

          <p className="af-eyebrow">Amart Furniture · Sign in</p>
          <h2 className="signin-welcome">Welcome back.</h2>

          {linkError === 'expired_link' && (
            <p className="form-error" role="alert">
              That link has expired. Request a new one below.
            </p>
          )}
          {linkError === 'invalid_link' && (
            <p className="form-error" role="alert">
              That link wasn&rsquo;t valid. Please request a fresh one.
            </p>
          )}

          {mode === 'password' && (
            <form onSubmit={onPasswordSubmit} noValidate>
              <div className="af-field">
                <label className="af-label" htmlFor="signin-email">
                  Email address
                </label>
                <input
                  id="signin-email"
                  className="af-input"
                  type="email"
                  autoComplete="username"
                  placeholder="you@amart.com.au"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <div className="af-rule" />
              </div>

              <div className="af-field">
                <div className="af-row">
                  <label className="af-label" htmlFor="signin-password">
                    Password
                  </label>
                  <Link className="af-forgot" to="/forgot-password">
                    Forgot?
                  </Link>
                </div>
                <input
                  id="signin-password"
                  className="af-input"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••••"
                  required
                  minLength={12}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <div className="af-rule" />
              </div>

              <div className="signin-check-row">
                <label className="af-check" data-on={keep}>
                  <input
                    className="af-check-input"
                    type="checkbox"
                    checked={keep}
                    onChange={(e) => setKeep(e.target.checked)}
                  />
                  <span className="af-box">
                    <CheckIcon />
                  </span>
                  <span>Keep me signed in on this device</span>
                </label>
              </div>

              {error && (
                <p className="form-error" role="alert">
                  {error}
                </p>
              )}

              <button className="af-btn" type="submit" disabled={submitting}>
                {submitting ? <span className="af-spin" /> : <>Sign in <ArrowIcon /></>}
              </button>
            </form>
          )}

          {mode === 'otp' && !codeSent && (
            <form onSubmit={onRequestCode} noValidate>
              <div className="af-field">
                <label className="af-label" htmlFor="signin-otp-email">
                  Email address
                </label>
                <input
                  id="signin-otp-email"
                  className="af-input"
                  type="email"
                  autoComplete="username"
                  placeholder="you@amart.com.au"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <div className="af-rule" />
              </div>

              {error && (
                <p className="form-error" role="alert">
                  {error}
                </p>
              )}

              <button className="af-btn" type="submit" disabled={submitting}>
                {submitting ? <span className="af-spin" /> : <>Email me a code <ArrowIcon /></>}
              </button>
            </form>
          )}

          {mode === 'otp' && codeSent && (
            <form onSubmit={onVerifyCode} noValidate>
              {info && (
                <p className="form-success" role="status">
                  {info}
                </p>
              )}
              <div className="af-field">
                <label className="af-label" htmlFor="signin-code">
                  6-digit code
                </label>
                <input
                  id="signin-code"
                  className="af-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={8}
                  placeholder="123456"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                />
                <div className="af-rule" />
              </div>

              {error && (
                <p className="form-error" role="alert">
                  {error}
                </p>
              )}

              <button className="af-btn" type="submit" disabled={submitting}>
                {submitting ? <span className="af-spin" /> : <>Verify &amp; sign in <ArrowIcon /></>}
              </button>
              <p className="signin-toggle">
                <button type="button" onClick={() => switchMode('otp')}>
                  Use a different email / resend
                </button>
              </p>
            </form>
          )}

          <p className="signin-toggle">
            {mode === 'password' ? (
              <button type="button" onClick={() => switchMode('otp')}>
                Email me a 6-digit code instead
              </button>
            ) : (
              <button type="button" onClick={() => switchMode('password')}>
                Sign in with a password instead
              </button>
            )}
          </p>
        </div>

        <p className="af-footer">
          Staff, supplier &amp; store access · Need help?{' '}
          <a href="mailto:support@amart.com.au">Contact support</a>
        </p>
      </div>

      {/* RIGHT — hero photo */}
      <div className="signin-right">
        <img className="signin-hero" src={heroUrl} alt="Amart Furniture superstore at dusk" />
        <div className="signin-scrim" />
        <div className="signin-right-foot">
          <div className="af-logo-chip">
            <img src={logoUrl} alt="Amart" />
          </div>
          <p className="signin-tagline">
            The home of furniture &amp; bedding
            <br />— across Australia.
          </p>
        </div>
      </div>
    </div>
  );
}
